<template>
  <div
    class="carrossel-imagens"
    :style="estiloDimensoes"
    @mouseenter="pausar"
    @mouseleave="reiniciar"
  >
    <div
      ref="trilha"
      class="carrossel-imagens-trilha"
      @scroll.passive="atualizarIndice"
      @touchstart="pausar"
      @touchend="reiniciar"
    >
      <div
        v-for="(imagem, index) in imagensExibidas"
        :key="imagem.id || (imagem.url + '-' + index)"
        class="carrossel-imagens-slide"
        :class="{'carrossel-imagens-clicavel': clicavel || zoom}"
        :role="clicavel || zoom ? 'button' : null"
        :tabindex="clicavel || zoom ? 0 : null"
        :aria-label="zoom && !clicavel ? 'Ampliar imagem' : null"
        @touchstart.passive="iniciarToque"
        @touchmove.passive="moverToque"
        @click="acionarImagem(imagem, index)"
        @keydown.enter.prevent="acionarImagem(imagem, index, true)"
        @keydown.space.prevent="acionarImagem(imagem, index, true)"
      >
        <imagem-carregavel
          :src="imagem.url"
          :lazy="index > 0"
          :prioridade="index === 0 ? 'high' : 'low'"
          :alt="imagem.alt || ''"
          class="carrossel-imagens-img"
        />
      </div>
    </div>

    <button
      v-if="zoom && imagemAtual"
      type="button"
      class="carrossel-imagens-acao carrossel-imagens-zoom"
      aria-label="Ampliar imagem atual"
      @click="$emit('zoom', imagemAtual, indiceAtual)"
    >
      <i class="fa fa-search-plus" aria-hidden="true"></i>
    </button>

    <template v-if="setas && temCarrossel">
      <button
        type="button"
        class="carrossel-imagens-acao carrossel-imagens-seta carrossel-imagens-anterior"
        aria-label="Imagem anterior"
        @click="navegar(-1)"
      >
        <i class="fa fa-chevron-left" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        class="carrossel-imagens-acao carrossel-imagens-seta carrossel-imagens-proxima"
        aria-label="Próxima imagem"
        @click="navegar(1)"
      >
        <i class="fa fa-chevron-right" aria-hidden="true"></i>
      </button>
    </template>

    <div v-if="temCarrossel" class="carrossel-imagens-indicadores" aria-label="Posição da imagem no carrossel">
      <button
        v-for="(imagem, index) in imagensExibidas"
        :key="'indicador-' + (imagem.id || imagem.url) + '-' + index"
        type="button"
        class="carrossel-imagens-indicador"
        :class="{'carrossel-imagens-indicador-ativo': index === indiceAtual}"
        :aria-label="'Exibir imagem ' + (index + 1)"
        :aria-current="index === indiceAtual ? 'true' : null"
        @click="irPara(index, true)"
      ></button>
    </div>
  </div>
</template>

<script>
  import ImagemCarregavel from "./ImagemCarregavel";
  export default {
    components: {ImagemCarregavel},
    name: 'CarrosselImagens',
    props: {
      imagens: {
        type: Array,
        default: () => []
      },
      fallback: {
        type: String,
        default: ''
      },
      altura: {
        type: String,
        default: '220px'
      },
      alturaDesktop: {
        type: String,
        default: ''
      },
      autoplay: {
        type: Number,
        default: 4000
      },
      setas: Boolean,
      zoom: Boolean,
      clicavel: Boolean
    },
    data() {
      return {
        indiceAtual: 0,
        intervalo: null,
        inicioToque: null,
        toqueDeslizado: false
      }
    },
    computed: {
      imagensValidas() {
        return this.imagens.filter(imagem => (
          imagem
          && typeof imagem.url === 'string'
          && imagem.url.trim()
        ));
      },
      imagensExibidas() {
        return this.imagensValidas.length
          ? this.imagensValidas
          : this.fallback
            ? [{id: 'fallback', url: this.fallback, alt: ''}]
            : [];
      },
      temCarrossel() {
        return this.imagensExibidas.length > 1;
      },
      imagemAtual() {
        return this.imagensExibidas[this.indiceAtual] || this.imagensExibidas[0] || null;
      },
      estiloDimensoes() {
        return {
          '--carrossel-altura': this.altura,
          '--carrossel-altura-desktop': this.alturaDesktop || this.altura
        };
      }
    },
    watch: {
      imagens: {
        immediate: true,
        handler() {
          this.indiceAtual = 0;
          this.$nextTick(() => {
            if (this.$refs.trilha) {
              this.$refs.trilha.scrollLeft = 0;
            }
            this.iniciar();
          });
        }
      },
      imagensExibidas() {
        if (this.indiceAtual >= this.imagensExibidas.length) {
          this.indiceAtual = Math.max(0, this.imagensExibidas.length - 1);
        }
        this.$nextTick(this.iniciar);
      }
    },
    beforeDestroy() {
      this.pausar();
    },
    methods: {
      iniciarToque(event) {
        const toque = event.touches[0];
        this.inicioToque = {x: toque.clientX, y: toque.clientY};
        this.toqueDeslizado = false;
      },
      moverToque(event) {
        if (!this.inicioToque) return;
        const toque = event.touches[0];
        if (Math.abs(toque.clientX - this.inicioToque.x) > 10
          || Math.abs(toque.clientY - this.inicioToque.y) > 10) {
          this.toqueDeslizado = true;
        }
      },
      acionarImagem(imagem, index, teclado = false) {
        if (this.toqueDeslizado && !teclado) {
          this.toqueDeslizado = false;
          return;
        }
        if (this.clicavel) {
          this.$emit('selecionar', imagem, index);
        } else if (this.zoom) {
          this.$emit('zoom', imagem, index);
        }
      },
      atualizarIndice(event) {
        const largura = event.currentTarget.clientWidth;
        if (largura) {
          this.indiceAtual = Math.round(event.currentTarget.scrollLeft / largura);
        }
      },
      irPara(index, reiniciar = false) {
        const trilha = this.$refs.trilha;
        if (!trilha) {
          return;
        }
        this.indiceAtual = index;
        trilha.scrollTo({left: trilha.clientWidth * index, behavior: 'smooth'});
        if (reiniciar) {
          this.reiniciar();
        }
      },
      navegar(direcao) {
        const total = this.imagensExibidas.length;
        this.irPara((this.indiceAtual + direcao + total) % total, true);
      },
      iniciar() {
        this.pausar();
        if (!this.temCarrossel || this.autoplay <= 0) {
          return;
        }
        this.intervalo = window.setInterval(() => {
          this.irPara((this.indiceAtual + 1) % this.imagensExibidas.length);
        }, this.autoplay);
      },
      pausar() {
        if (this.intervalo) {
          window.clearInterval(this.intervalo);
          this.intervalo = null;
        }
      },
      reiniciar() {
        this.iniciar();
      }
    }
  }
</script>

<style scoped>
  .carrossel-imagens {
    position: relative;
    width: 100%;
    height: var(--carrossel-altura);
    overflow: hidden;
  }
  .carrossel-imagens-trilha {
    display: flex;
    width: 100%;
    height: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }
  .carrossel-imagens-trilha::-webkit-scrollbar {
    display: none;
  }
  .carrossel-imagens-slide {
    flex: 0 0 100%;
    width: 100%;
    height: 100%;
    scroll-snap-align: start;
    scroll-snap-stop: always;
  }
  .carrossel-imagens-clicavel {
    cursor: pointer;
  }
  .carrossel-imagens-img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
  .carrossel-imagens-indicadores {
    position: absolute;
    left: 50%;
    bottom: 12px;
    display: flex;
    align-items: center;
    transform: translateX(-50%);
    z-index: 1;
  }
  .carrossel-imagens-indicador {
    width: 8px;
    height: 8px;
    margin: 0 2px;
    padding: 0;
    border: 1px solid #fff;
    border-radius: 50%;
    background-color: transparent;
    opacity: 1;
    cursor: pointer;
  }
  .carrossel-imagens-indicador-ativo {
    background-color: #fff;
  }
  .carrossel-imagens-acao {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.9);
    color: #111;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;
    z-index: 2;
    cursor: pointer;
  }
  .carrossel-imagens:hover .carrossel-imagens-acao,
  .carrossel-imagens-acao:focus-visible {
    opacity: 1;
    pointer-events: auto;
  }
  .carrossel-imagens-zoom {
    top: 10px;
    right: 10px;
  }
  .carrossel-imagens-seta {
    top: 50%;
    transform: translateY(-50%);
  }
  .carrossel-imagens-anterior {
    left: 10px;
  }
  .carrossel-imagens-proxima {
    right: 10px;
  }
  @media (min-width: 768px) {
    .carrossel-imagens {
      height: var(--carrossel-altura-desktop);
    }
  }
  @media (hover: none), (pointer: coarse) {
    .carrossel-imagens-acao {
      display: none;
    }
  }
</style>
